import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { ConfirmModal } from "../../components/ConfirmModal";
import { History, Calendar, Plus, Trash2, Edit2, ExternalLink } from "lucide-react";

interface PastTraining {
  id: number;
  title: string;
  date: string;
  time: string | null;
  recording_link: string;
  password: string | null;
  description: string | null;
  trainer_name: string | null;
  type: string;
}

export function PastSessionsTab() {
  const [trainings, setTrainings] = useState<PastTraining[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    title: "",
    date: "",
    time: "",
    recording_link: "",
    password: "",
    description: "",
    trainer_name: "",
  });

  useEffect(() => {
    loadProfile();
    loadTrainings();
  }, []);

  async function loadProfile() {
    const { data } = await supabase.auth.getUser();
    const userId = data.user?.id;
    if (!userId) return;

    const { data: p } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    setProfile(p);
  }

  function canManage() {
    if (!profile) return false;
    if (profile.role === "superadmin") return true;
    if (profile.role === "admin" && profile.admin_level >= 2) return true;
    return false;
  }

  async function loadTrainings() {
    setLoading(true);

    const { data } = await supabase
      .from("trainings")
      .select("*")
      .eq("type", "past")
      .order("date", { ascending: false });

    if (data) setTrainings(data);

    setLoading(false);
  }

  async function handleSubmit() {
  if (!formData.title || !formData.date || !formData.time || !formData.recording_link || !formData.trainer_name) {
    alert("Please fill all required fields.");
    return;
  }

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) return;

  if (editingId) {
    const { data, error } = await supabase
      .from("trainings")
      .update({
        ...formData,
      })
      .eq("id", editingId)
      .select()
      .single();

    if (error) {
      console.error(error);
      return;
    }

    setTrainings(prev =>
      prev.map(t => (t.id === editingId ? data : t))
    );

    setEditingId(null);
  } else {
    const { data, error } = await supabase
      .from("trainings")
      .insert({
        ...formData,
        type: "past",
        author_id: userId,
      })
      .select()
      .single();

    if (error) {
      console.error(error);
      return;
    }

    setTrainings(prev => [data, ...prev]);
  }

  setFormData({
    title: "",
    date: "",
    time: "",
    recording_link: "",
    password: "",
    description: "",
    trainer_name: "",
  });
}

  async function deleteTraining(id: number) {
    const { error } = await supabase
      .from("trainings")
      .delete()
      .eq("id", id);

    if (!error) {
      setTrainings(prev => prev.filter(t => t.id !== id));
    }

    setDeleteConfirmId(null);
  }

  function startEdit(training: PastTraining) {
    setEditingId(training.id);
    setFormData({
      title: training.title,
      date: training.date,
      time: training.time || "",
      recording_link: training.recording_link,
      password: training.password || "",
      description: training.description || "",
      trainer_name: training.trainer_name || "",
    });
  }

  function cancelEdit() {
    setEditingId(null);
  }

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-6">

      <h1 className="text-2xl font-bold text-gray-900">
        Past Training Sessions
      </h1>

      {canManage() && (
        <div className="bg-white p-6 rounded-xl shadow-md space-y-4">
          <input
            type="text"
            placeholder="Title"
            value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />

          <div className="flex gap-3">
            <input
              type="date"
              value={formData.date}
              onChange={e => setFormData({ ...formData, date: e.target.value })}
              className="border rounded px-3 py-2"
            />
            <input
              type="time"
              value={formData.time}
              onChange={e => setFormData({ ...formData, time: e.target.value })}
              className="border rounded px-3 py-2"
            />
          </div>

          <input
            type="url"
            placeholder="Recording Link"
            value={formData.recording_link}
            onChange={e => setFormData({ ...formData, recording_link: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />

          <input
            type="text"
            placeholder="Password"
            value={formData.password}
            onChange={e => setFormData({ ...formData, password: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />

          <input
            type="text"
            placeholder="Trainer Name"
            value={formData.trainer_name}
            onChange={e => setFormData({ ...formData, trainer_name: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />

          <input
            type="text"
            placeholder="Description"
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />

          <button
            onClick={handleSubmit}
            className="bg-nhs-blue text-white px-4 py-2 rounded-lg"
          >
            {editingId ? "Update Training" : "Add Training"}
          </button>
        </div>
      )}

      {trainings.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <History className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">
            No past trainings
          </h3>
        </div>
      ) : (
        <div className="grid gap-4">
          {trainings.map((training) => (
  <div
    key={training.id}
    className="bg-nhs-blue text-white p-5 rounded-xl shadow-md flex justify-between items-start"
  >
    <div className="flex-1">

      {editingId === training.id ? (

        /* ================= INLINE EDIT MODE ================= */

        <div className="space-y-3">

          <input
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            className="w-full border rounded px-3 py-2 text-sm text-black"
          />

          <div className="flex gap-3">
            <input
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
              className="border rounded px-3 py-2 text-sm text-black"
            />

            <input
              type="time"
              value={formData.time}
              onChange={(e) =>
                setFormData({ ...formData, time: e.target.value })
              }
              className="border rounded px-3 py-2 text-sm text-black"
            />
          </div>

          <input
            type="url"
            value={formData.recording_link}
            onChange={(e) =>
              setFormData({ ...formData, recording_link: e.target.value })
            }
            className="w-full border rounded px-3 py-2 text-sm text-black"
          />

          <input
            type="text"
            value={formData.trainer_name}
            onChange={(e) =>
              setFormData({ ...formData, trainer_name: e.target.value })
            }
            className="w-full border rounded px-3 py-2 text-sm text-black"
          />

          <input
            type="text"
            value={formData.password}
            onChange={(e) =>
              setFormData({ ...formData, password: e.target.value })
            }
            className="w-full border rounded px-3 py-2 text-sm text-black"
          />

          <input
            type="text"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            className="w-full border rounded px-3 py-2 text-sm text-black"
          />

          <div className="flex gap-3 mt-2">
            <button
              onClick={handleSubmit}
              className="bg-white text-nhs-blue px-3 py-1 rounded font-semibold"
            >
              Save
            </button>

            <button
              onClick={cancelEdit}
              className="bg-white/30 text-white px-3 py-1 rounded"
            >
              Cancel
            </button>
          </div>

        </div>

      ) : (

        /* ================= DISPLAY MODE ================= */

        <>
          <h3 className="font-semibold text-lg">
            {training.title}
          </h3>

          <div className="text-sm mt-1">
            {training.date} • {training.time}
          </div>

          <div className="text-sm mt-1">
            Trainer: {training.trainer_name}
          </div>

          {training.password && (
            <div className="text-sm mt-1">
              Password: {training.password}
            </div>
          )}

          {training.description && (
            <div className="text-sm mt-1">
              {training.description}
            </div>
          )}
        </>
      )}
    </div>

    {canManage() && editingId !== training.id && (
      <div className="flex gap-2 ml-4">
        <a
          href={training.recording_link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-white underline font-semibold"
        >
          <ExternalLink size={16} />
        </a>

        <button
          onClick={() => startEdit(training)}
          className="hover:text-blue-200 transition-colors"
        >
          <Edit2 size={16} />
        </button>

        <button
          onClick={() => setDeleteConfirmId(training.id)}
          className="hover:text-red-300 transition-colors"
        >
          <Trash2 size={16} />
        </button>
      </div>
    )}
  </div>
))}
        </div>
      )}

      <ConfirmModal
        open={deleteConfirmId !== null}
        title="Delete Past Training"
        message="Are you sure you want to delete this past training?"
        variant="danger"
        confirmText="Delete"
        onConfirm={() => deleteConfirmId && deleteTraining(deleteConfirmId)}
        onCancel={() => setDeleteConfirmId(null)}
      />
    </div>
  );
}